/**
 * Shared ACC hub → project → folder → file browse state (Phase 2c).
 * Used by the standalone left-panel browser and Forma Files screen.
 */
import { create } from "zustand";
import type {
  AccFolderEntry,
  AccHub,
  AccProject,
  AccProjectDetails,
} from "../services/dataManagement";
import type { DmAdsRegion } from "../services/dataManagement/region";

export interface AccFileSelection {
  hubId: string;
  hubName: string;
  hubRegion?: DmAdsRegion;
  projectId: string;
  projectName: string;
  itemId: string;
  fileName: string;
  folderPath: string;
}

export interface FolderCrumb {
  id: string;
  name: string;
}

export interface AccFileRow {
  itemId: string;
  fileName: string;
  extension: string;
}

interface AccBrowseState {
  hubs: AccHub[];
  projects: AccProject[];
  hub: AccHub | null;
  project: AccProject | null;
  projectDetails: AccProjectDetails | null;
  crumbs: FolderCrumb[];
  sidebarFolders: AccFolderEntry[];
  fileRows: AccFileRow[];
  activeFolderId: string | null;
  selectedItemId: string | null;
  selection: AccFileSelection | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  setHubs: (hubs: AccHub[]) => void;
  setProjects: (projects: AccProject[]) => void;
  setHub: (hub: AccHub | null) => void;
  setProject: (project: AccProject | null) => void;
  setProjectDetails: (details: AccProjectDetails | null) => void;
  setCrumbs: (crumbs: FolderCrumb[]) => void;
  setSidebarFolders: (folders: AccFolderEntry[]) => void;
  setFileRows: (rows: AccFileRow[]) => void;
  setActiveFolderId: (id: string | null) => void;
  setSelectedItemId: (id: string | null) => void;
  setSelection: (selection: AccFileSelection | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInitialized: (initialized: boolean) => void;
  resetBrowse: () => void;
}

export const useAccBrowseStore = create<AccBrowseState>(set => ({
  hubs: [],
  projects: [],
  hub: null,
  project: null,
  projectDetails: null,
  crumbs: [],
  sidebarFolders: [],
  fileRows: [],
  activeFolderId: null,
  selectedItemId: null,
  selection: null,
  loading: false,
  error: null,
  initialized: false,
  setHubs: hubs => set({ hubs }),
  setProjects: projects => set({ projects }),
  setHub: hub => set({ hub }),
  setProject: project => set({ project }),
  setProjectDetails: projectDetails => set({ projectDetails }),
  setCrumbs: crumbs => set({ crumbs }),
  setSidebarFolders: sidebarFolders => set({ sidebarFolders }),
  setFileRows: fileRows => set({ fileRows }),
  setActiveFolderId: activeFolderId => set({ activeFolderId }),
  setSelectedItemId: selectedItemId => set({ selectedItemId }),
  setSelection: selection => set({ selection }),
  setLoading: loading => set({ loading }),
  setError: error => set({ error }),
  setInitialized: initialized => set({ initialized }),
  resetBrowse: () =>
    set({
      projects: [],
      hub: null,
      project: null,
      projectDetails: null,
      crumbs: [],
      sidebarFolders: [],
      fileRows: [],
      activeFolderId: null,
      selectedItemId: null,
      selection: null,
      error: null,
    }),
}));
